import { computed, onMounted, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL } from '@/shared/data/actionCableChannels.js'
import { useActionCable } from '@/shared/composables/useActionCable'
import { useNotificationsStore } from '@/shared/stores/notifications.js'

export type SidebarCounts = {
  messages: number
  notifications: number
  pendingSessionRequests: number | null
  boardInvites: number | null
  liveMentors: number | null
  // A mentor's answers on the question pages, and the ones that came after
  // the mentor's last visit to the list. null for a mentee and with the
  // question-pages flag off.
  answers: number | null
  newAnswers: number | null
  // The lines Claude added to the member's brief after the last visit to
  // /memory. null for a member with no recorded call yet, and for a mentor.
  memoryNewLines: number | null
  // The new items of the Direction page the member did not see yet. null
  // with the direction flag off and for a mentor: no "Direction" row then.
  directionNew: number | null
}

type SidebarProp =
  | {
      pendingSessionRequests: number | null
      boardInvites?: number | null
      liveMentors: number | null
      answers?: number | null
      newAnswers?: number | null
      memoryNewLines?: number | null
      directionNew?: number | null
    }
  | null
  | undefined

// The numbers next to the sidebar items. Messages come from the same live
// channel as the top bar, notifications from the notifications store, and a
// mentor's unanswered session requests and the live mentors from the shared
// `sidebar` prop.
// SidebarNav calls this once and passes the counts down, so the channel has
// one subscriber, and only while the sidebar is on. With the flag off the top
// bar keeps its own subscription.
export const useSidebarCounts = () => {
  const page = usePage()
  const notificationsStore = useNotificationsStore()
  const unreadMessages = ref(0)

  const { subscribeChannel } = useActionCable({
    channelName: PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL,
    onReceived: (data) => {
      const payload = data as { notifications: string }
      const notifications = JSON.parse(payload.notifications) as {
        read_at: string | null
      }[]
      unreadMessages.value = notifications.filter(
        (notification) => !notification.read_at,
      ).length
    },
  })

  onMounted(() => {
    subscribeChannel()
  })

  const markMessagesAsRead = () => {
    axios
      .post('/notifications/mark_all_as_read_by_type', { type: 'messages' })
      .catch(() => {
        // The badge comes back from the channel if the server did not mark.
      })
    unreadMessages.value = 0
  }

  const markNotificationsAsSeen = () => {
    if (notificationsStore.unseenCount === 0) return
    notificationsStore.markAllNotificationsAsSeen()
  }

  const counts = computed<SidebarCounts>(() => {
    const sidebar = (page.props.shared as { sidebar?: SidebarProp }).sidebar
    return {
      messages: unreadMessages.value,
      notifications: notificationsStore.unseenCount,
      pendingSessionRequests: sidebar?.pendingSessionRequests ?? null,
      boardInvites: sidebar?.boardInvites ?? null,
      liveMentors: sidebar?.liveMentors ?? null,
      answers: sidebar?.answers ?? null,
      newAnswers: sidebar?.newAnswers ?? null,
      memoryNewLines: sidebar?.memoryNewLines ?? null,
      directionNew: sidebar?.directionNew ?? null,
    }
  })

  return { counts, markMessagesAsRead, markNotificationsAsSeen }
}
